import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from "react";
import { paletteColor } from "../lib/palette";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Clock, FileText, MessageSquare, Plus, Send, Sun, Zap } from "lucide-react";
import { useStore } from "../lib/store";
import type { Lead, Membership, Stage } from "../lib/types";
import { HEAT_BANDS, PERIOD_OPTIONS, leadHeat, rangeLabel, type PeriodId } from "../features/leads/list";
import { leadReceivedCents, type FinanceReceipt } from "../features/finance/contracts";
import type { Notice } from "../features/notifications/feed";
import {
  attentionItems,
  conversionText,
  dailySeries,
  dayLabels,
  deltaTone,
  funnelFlow,
  goalOrbit,
  homeInsights,
  homeKpis,
  homeRange,
  revenuePath,
  smoothPath,
  todayHint,
  todayTasks,
  type DayRange,
  type GoalOrbit,
} from "../features/home/metrics";
import { firstName, useProfileIdentity } from "../app/useProfileIdentity";
import { appPath } from "../app/routes";
import { CompactSelect } from "../ui/CompactSelect";
import { HelpPopover, type MetricExplanation } from "../ui/HelpPopover";
import { Spark } from "../ui/Spark";
import { avatarGradient, avatarInitials } from "../ui/avatar-identity";
import { CustomPeriod, money } from "./lista-popovers";
import { CosmosAiIcon } from "../ui/CosmosAiIcon";
import { ReportDialog } from "./ReportDialog";
import "./inicio.css";

/**
 * Início — porte literal da `#page-inicio` do Zênite: saudação com fichas de
 * período, quatro indicadores com sparklines, visão da receita com marcador,
 * insights (regras publicadas, não IA), conversão do funil, meta do trimestre
 * em dois anéis, tarefas de hoje, o que requer atenção e top leads.
 *
 * Toda fórmula vive em `features/home/metrics.ts` (§10). "Novo relatório"
 * baixa o período em CSV com os mesmos números da tela; o que o produto ainda
 * não faz — perguntar à IA daqui — aparece desabilitado com o motivo, nunca
 * como controle falso (§18).
 */
const HOME_PERIODS: PeriodId[] = ["hoje", "semana", "mes", "tri", "custom"];
const DEFAULT_HOME_PERIOD: PeriodId = "semana";

const moneyCents = (cents: number) => money(Math.round(cents / 100));
const int = new Intl.NumberFormat("pt-BR");

function periodHint(period: PeriodId, range: DayRange): string {
  switch (period) {
    case "hoje":
      return "hoje";
    case "semana":
      return "últimos 7 dias";
    case "mes":
      return "últimos 30 dias";
    case "tri":
      return "este trimestre";
    default:
      return rangeLabel(range).replace(" – ", " a ");
  }
}

function isPeriod(value: string | null): value is PeriodId {
  return HOME_PERIODS.includes(value as PeriodId);
}

/** Contagem animada do protótipo (`countUp`, 900ms); pula com movimento reduzido. */
function useCountUp(target: number): number {
  const [value, setValue] = useState(target);
  useEffect(() => {
    const reduced =
      typeof window.matchMedia !== "function" || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setValue(target);
      return;
    }
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const progress = Math.min((now - start) / 900, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * ease));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return value;
}

/** Largura útil de um elemento, para gráficos SVG na largura real do cartão. */
function useWidth<T extends HTMLElement | SVGElement>(minimum: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(minimum);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => setWidth(Math.max(minimum, Math.round(element.getBoundingClientRect().width) || 0));
    measure();
    if (typeof ResizeObserver !== "function") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [minimum]);
  return { ref, width };
}

// --- indicadores ---------------------------------------------------------------

function KpiCard({
  label,
  help,
  value,
  delta,
  tone,
  spark,
  sparkColor,
  index,
  featured,
  sparkLabel,
}: {
  label: string;
  help: MetricExplanation[];
  value: string;
  delta: string;
  tone: "up" | "down" | "flat";
  spark: readonly number[];
  sparkColor: string;
  index: number;
  featured?: boolean;
  sparkLabel: string;
}) {
  return (
    <div className={featured ? "card kpi kpi-featured" : "card kpi"}>
      <div className="label">
        {label}
        <HelpPopover about={label.toLowerCase()} label={`O que este bloco mede: ${label}`} items={help} align="end" />
      </div>
      <div className="kpi-row">
        <div>
          <div className="value num">{value}</div>
          <br />
          <span className={`delta ${tone}`}>{delta}</span>
        </div>
        <Spark points={spark} color={sparkColor} label={sparkLabel} index={index} />
      </div>
    </div>
  );
}

// --- visão da receita ---------------------------------------------------------

interface ChartData {
  labels: string[];
  /** Receita por dia, em reais. */
  rev: number[];
  /** Primeiras mensalidades por dia, em reais. */
  leadRevenue: number[];
  leadCounts: number[];
}

function RevenueChart({ data }: { data: ChartData }) {
  const { ref, width: W } = useWidth<HTMLDivElement>(560);
  const H = 330;
  const padL = 52;
  const padR = 20;
  const padT = 36;
  const padB = 30;
  const iw = W - padL - padR;
  const ih = H - padT - padB;
  const n = Math.max(1, data.labels.length);
  const maxR = Math.max(...data.rev, 1) * 1.22;
  const xs = (index: number) => (n > 1 ? padL + (iw * index) / (n - 1) : padL + iw / 2);
  const yR = (value: number) => padT + ih - (ih * value) / maxR;

  const [marker, setMarker] = useState<{ index: number; serie: "rev" | "leads" }>({ index: n - 1, serie: "rev" });
  useEffect(() => setMarker({ index: n - 1, serie: "rev" }), [n, data]);

  const leadPoints = data.leadRevenue.map((value, index) => [xs(index), yR(value)] as [number, number]);
  const revPoints = data.rev.map((value, index) => [xs(index), yR(value)] as [number, number]);
  const lline = revenuePath(leadPoints);
  const rline = revenuePath(revPoints);
  const llineBack = revenuePath([...leadPoints].reverse());

  const gridLines = [0, 1, 2, 3, 4].map((step) => {
    const y = padT + ih - (ih * step) / 4;
    const value = (maxR * step) / 4;
    return { y, text: value >= 1000 ? `${Math.round(value / 100) / 10}k` : String(Math.round(value)) };
  });

  // Rótulos do eixo X: todos até 10 dias; acima disso, um a cada intervalo.
  const labelStep = Math.max(1, Math.ceil(n / 10));

  const isLeads = marker.serie === "leads";
  const series = isLeads ? data.leadRevenue : data.rev;
  const mi = Math.min(n - 1, Math.max(0, marker.index));
  const mx = xs(mi);
  const my = yR(series[mi] ?? 0);
  const comparison = isLeads ? data.leadCounts : data.rev;
  const current = comparison[mi] || 0;
  const previous = mi > 0 ? comparison[mi - 1] || 0 : 0;
  let deltaLabel = "—";
  if (mi > 0 && previous > 0) {
    const change = Math.round(((current - previous) / previous) * 100);
    deltaLabel = `${change >= 0 ? "↑" : "↓"} ${Math.abs(change)}%`;
  } else if (mi > 0 && current === 0 && previous === 0) {
    deltaLabel = "0%";
  }
  const count = data.leadCounts[mi] || 0;
  const line1 = isLeads ? `${count} ${count === 1 ? "lead" : "leads"}` : money(series[mi] ?? 0);
  const pillWidth = Math.max(line1.length * 7.2, deltaLabel.length * 5.6) + 20;
  const pillX = Math.min(Math.max(mx - pillWidth / 2, padL), W - padR - pillWidth);
  const markerColor = isLeads ? "var(--accent-2)" : "var(--accent)";

  const onMove = (event: ReactMouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const vx = ((event.clientX - rect.left) / rect.width) * W;
    const vy = ((event.clientY - rect.top) / rect.height) * H;
    const index = Math.min(n - 1, Math.max(0, Math.round(((vx - padL) / iw) * (n - 1))));
    const serie = Math.abs(yR(data.leadRevenue[index] ?? 0) - vy) < Math.abs(yR(data.rev[index] ?? 0) - vy) ? "leads" : "rev";
    setMarker({ index, serie });
  };

  return (
    <div ref={ref} className="chart-host">
      <svg
        className="chart"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Receita em área e leads em linha"
        onMouseMove={onMove}
        onMouseLeave={() => setMarker({ index: n - 1, serie: "rev" })}
      >
        {gridLines.map((line) => (
          <g key={line.y}>
            <line x1={padL} y1={line.y} x2={W - padR} y2={line.y} stroke="var(--chart-grid)" strokeWidth="1" />
            <text x={padL - 8} y={line.y + 4} textAnchor="end" fontSize="10" fill="var(--faint)" fontFamily="JetBrains Mono,monospace">
              {line.text}
            </text>
          </g>
        ))}
        {data.labels.map((label, index) =>
          index % labelStep === 0 || index === n - 1 ? (
            <text key={label + index} x={xs(index)} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--muted)">
              {label}
            </text>
          ) : null,
        )}
        <defs>
          <linearGradient id="zn-chart-rev" x1="0" y1="0" x2="0" y2="1">
            <stop style={{ stopColor: "var(--grad-b)", stopOpacity: .42 }} />
            <stop offset="1" style={{ stopColor: "var(--grad-a)", stopOpacity: .16 }} />
          </linearGradient>
          <linearGradient id="zn-chart-leads" x1="0" y1="0" x2="0" y2="1">
            <stop style={{ stopColor: "var(--accent-2)", stopOpacity: .34 }} />
            <stop offset="1" style={{ stopColor: "var(--accent-2)", stopOpacity: .07 }} />
          </linearGradient>
        </defs>
        {/* faixa da receita: entre a linha da receita e a linha de leads */}
        <path className="area-layer" d={`${rline}${llineBack.replace(/^M/, " L")} Z`} fill="url(#zn-chart-rev)" />
        {/* camada de leads: da linha de leads até a base */}
        <path
          className="area-layer"
          d={`${lline} L${xs(n - 1)},${padT + ih} L${xs(0)},${padT + ih} Z`}
          fill="url(#zn-chart-leads)"
          style={{ animationDelay: ".15s" }}
        />
        <path className="lead-path" d={lline} pathLength={1} fill="none" stroke="var(--accent-2)" strokeWidth="1.8" opacity=".9" style={{ animationDelay: ".4s" }} />
        <path className="lead-path" d={rline} pathLength={1} fill="none" stroke="var(--accent)" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
        <g className="marker">
          <line x1={mx} x2={mx} y1={my + 5} y2={padT + ih} stroke="color-mix(in srgb, var(--accent-2) 45%, transparent)" strokeWidth="1" />
          <circle cx={mx} cy={my} r="4" fill="var(--surface)" stroke={markerColor} strokeWidth="2" />
          <rect x={pillX} y={my - 49} width={pillWidth} height="36" rx="9" fill="var(--surface-3)" stroke={`color-mix(in srgb, ${markerColor} 60%, transparent)`} strokeWidth="1.2" />
          <text x={pillX + pillWidth / 2} y={my - 34} textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--ink)" fontFamily="JetBrains Mono,monospace">
            {line1}
          </text>
          <text x={pillX + pillWidth / 2} y={my - 20} textAnchor="middle" fontSize="9.5" fill="var(--muted)" fontFamily="Instrument Sans,sans-serif">
            {deltaLabel}
          </text>
        </g>
      </svg>
    </div>
  );
}

// --- conversão do funil ---------------------------------------------------------

function FunnelChart({ ratios }: { ratios: readonly number[] }) {
  const { ref, width: W } = useWidth<HTMLDivElement>(480);
  const H = 150;
  const c = H / 2;
  const hmax = H - 16;
  const n = Math.max(1, ratios.length);
  const h = (ratio: number) => Math.max(0.05, ratio) * hmax;
  const top: Array<[number, number]> = [[0, c - h(ratios[0] ?? 0) / 2]];
  const bottom: Array<[number, number]> = [[0, c + h(ratios[0] ?? 0) / 2]];
  ratios.forEach((ratio, index) => {
    const x = (W * (index + 0.5)) / n;
    top.push([x, c - h(ratio) / 2]);
    bottom.push([x, c + h(ratio) / 2]);
  });
  const last = ratios[ratios.length - 1] ?? 0;
  top.push([W, c - h(last) / 2]);
  bottom.push([W, c + h(last) / 2]);
  const d = `${smoothPath(top)}${smoothPath([...bottom].reverse()).replace(/^M/, " L")} Z`;
  return (
    <div ref={ref} className="fn-chart-host">
      <svg className="funnel-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Fluxo de leads por etapa do funil">
        {ratios.map((_, index) => {
          const x = (W * index) / n;
          return (
            <g key={index}>
              {index % 2 === 1 && <rect x={x} y="0" width={W / n} height={H} fill="var(--accent)" opacity=".04" />}
              {index > 0 && <line x1={x} y1="0" x2={x} y2={H} stroke="var(--border-soft)" strokeWidth="1" />}
            </g>
          );
        })}
        <defs>
          <linearGradient id="zn-funnel-fill" x1="0" y1="0" x2="1" y2="0">
            <stop style={{ stopColor: "var(--grad-a)", stopOpacity: .5 }} />
            <stop offset="1" style={{ stopColor: "var(--grad-b)", stopOpacity: .24 }} />
          </linearGradient>
        </defs>
        <path className="area-layer" d={d} fill="url(#zn-funnel-fill)" stroke="color-mix(in srgb, var(--accent) 45%, transparent)" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

/** Atraso de cada etapa sincronizado com a curva de revelação da área (protótipo). */
function stageDelay(progress: number): number {
  const flowStart = 250;
  const flowDuration = 1500;
  if (progress === 0) return flowStart;
  let lo = 0;
  let hi = 1;
  for (let step = 0; step < 20; step += 1) {
    const t = (lo + hi) / 2;
    const y = t * t * (3 - 2 * t);
    if (y < progress) lo = t;
    else hi = t;
  }
  const t = (lo + hi) / 2;
  const u = 1 - t;
  return flowStart + flowDuration * (3 * u * u * t * 0.45 + 3 * u * t * t * 0.2 + t * t * t);
}

// --- meta do trimestre -----------------------------------------------------------

const ORBIT_TICKS = 64;
const ORBIT_R1 = 71;
const ORBIT_R2 = 82;
const ORBIT_CENTER = 95;

/**
 * Anel de tracinhos do protótipo (`setOrbit`): acende os traços em sequência
 * por 1,4s com o planeta encaixado no último aceso. Movimento reduzido: estado
 * final imediato.
 */
function OrbitGauge({ ratio, colors, sub }: { ratio: number; colors: [string, string]; sub: string }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const reduced =
      typeof window.matchMedia !== "function" || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setProgress(1);
      return;
    }
    setProgress(0);
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const p = Math.min((now - start) / 1400, 1);
      setProgress(1 - Math.pow(1 - p, 3));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [ratio]);

  const lit = Math.round(ratio * ORBIT_TICKS * progress);
  const angle = -Math.PI / 2 + (2 * Math.PI * Math.max(0, lit - 1)) / ORBIT_TICKS;
  const rp = ORBIT_R2 + 7;
  return (
    <div className="orbit-wrap">
      <svg viewBox="0 0 190 190" aria-hidden="true">
        <g>
          {Array.from({ length: ORBIT_TICKS }, (_, index) => {
            const a = -Math.PI / 2 + (2 * Math.PI * index) / ORBIT_TICKS;
            const mix = Math.round((index / ORBIT_TICKS) * 100);
            return (
              <line
                key={index}
                x1={(ORBIT_CENTER + ORBIT_R1 * Math.cos(a)).toFixed(1)}
                y1={(ORBIT_CENTER + ORBIT_R1 * Math.sin(a)).toFixed(1)}
                x2={(ORBIT_CENTER + ORBIT_R2 * Math.cos(a)).toFixed(1)}
                y2={(ORBIT_CENTER + ORBIT_R2 * Math.sin(a)).toFixed(1)}
                stroke={index < lit ? `color-mix(in srgb, ${colors[1]} ${mix}%, ${colors[0]})` : "var(--surface-3)"}
                strokeWidth="3.4"
                strokeLinecap="round"
              />
            );
          })}
        </g>
        <circle
          className="orbit-planet"
          cx={(ORBIT_CENTER + rp * Math.cos(angle)).toFixed(1)}
          cy={(ORBIT_CENTER + rp * Math.sin(angle)).toFixed(1)}
          r="6"
          fill="var(--accent-2)"
          stroke="var(--surface)"
          strokeWidth="2.5"
        />
      </svg>
      <div className="orbit-center">
        <div className="pct num">{Math.round(ratio * 100 * progress)}%</div>
        <div className="sub">{sub}</div>
      </div>
    </div>
  );
}

function OrbitFootGoal({ orbit }: { orbit: GoalOrbit }) {
  if (!orbit.goal) {
    return (
      <p className="orbit-foot">
        Nenhuma meta definida para <b>{orbit.label}</b>.<br />
        Defina a meta em Configurações › Metas.
      </p>
    );
  }
  const goal = orbit.goal.amountCents;
  const gap = Math.abs(goal - orbit.achievedCents);
  return (
    <p className="orbit-foot">
      <b className="num">{moneyCents(orbit.achievedCents)}</b> de <span className="num">{moneyCents(goal)}</span>
      <br />
      {orbit.achievedCents < goal ? "faltam" : "acima da meta em"} <b className="num">{moneyCents(gap)}</b> em {orbit.remainingDays} dias
    </p>
  );
}

function OrbitFootPace({ orbit }: { orbit: GoalOrbit }) {
  if (!orbit.goal) {
    return <p className="orbit-foot">sem meta, não há ritmo esperado a comparar.</p>;
  }
  const diff = orbit.achievedCents - orbit.expectedCents;
  return (
    <p className="orbit-foot">
      esperado até hoje: <b className="num">{moneyCents(orbit.expectedCents)}</b>
      <br />
      você está <b className="num">{moneyCents(Math.abs(diff))}</b> {diff >= 0 ? "à frente" : "atrás"}
    </p>
  );
}

// --- top leads -----------------------------------------------------------------------

interface TopLead {
  lead: Lead;
  stage?: Stage;
  ltvCents: number;
  heat: number;
}

function topLeads({
  leads,
  memberships,
  stages,
  receipts,
  range,
  now,
}: {
  leads: readonly Lead[];
  memberships: readonly Membership[];
  stages: readonly Stage[];
  receipts: readonly FinanceReceipt[];
  range: DayRange;
  now: string;
}): TopLead[] {
  const leadById = new Map(leads.filter((lead) => !lead.archived).map((lead) => [lead.id, lead]));
  const inPeriod = new Map<string, number>();
  for (const receipt of receipts) {
    if (receipt.receivedOn < range.from || receipt.receivedOn > range.to) continue;
    if (!leadById.has(receipt.leadId)) continue;
    inPeriod.set(receipt.leadId, (inPeriod.get(receipt.leadId) ?? 0) + receipt.amountCents);
  }
  const membershipByLead = new Map(memberships.map((membership) => [membership.leadId, membership]));
  const topValueByFunnel = new Map<string, number>();
  for (const membership of memberships) {
    const lead = leadById.get(membership.leadId);
    if (!lead) continue;
    topValueByFunnel.set(membership.funnelId, Math.max(topValueByFunnel.get(membership.funnelId) ?? 0, lead.potentialValue));
  }
  return [...inPeriod.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([leadId]) => {
      const lead = leadById.get(leadId)!;
      const membership = membershipByLead.get(leadId);
      return {
        lead,
        stage: membership ? stages.find((stage) => stage.id === membership.stageId) : undefined,
        ltvCents: leadReceivedCents(receipts, leadId),
        heat: leadHeat({
          lead,
          membership,
          stages,
          funnelTopValue: membership ? (topValueByFunnel.get(membership.funnelId) ?? 0) : 0,
          now,
        }),
      };
    });
}

/** Ícone por categoria do aviso, como no protótipo: relógio, sol, documento, raio, balão. */
function NoticeIcon({ category }: { category: Notice["category"] }) {
  switch (category) {
    case "tarefa":
      return <Clock size={17} />;
    case "lead":
      return <Sun size={17} />;
    case "financeiro":
      return <FileText size={17} />;
    case "automacao":
      return <Zap size={17} />;
    default:
      return <MessageSquare size={17} />;
  }
}

function healthOf(heat: number): { label: string; tone: "good" | "warn" | "crit" } {
  if (heat >= 80) return { label: "Saudável", tone: "good" };
  if (heat >= 60) return { label: "Atenção", tone: "warn" };
  return { label: "Em risco", tone: "crit" };
}

// --- página --------------------------------------------------------------------------

const KPI_HELP = {
  receita: [
    { term: "Fórmula", description: "Soma de todos os pagamentos recebidos no período, incluindo mensalidades, avulsos e extras." },
    { term: "Não entra", description: "Valores previstos e faturas em aberto." },
    { term: "Comparação", description: "Variação percentual contra o período imediatamente anterior, de igual duração." },
  ],
  leads: [
    { term: "Fórmula", description: "Leads criados no período, de qualquer origem; a data considerada é a criação do cadastro." },
    { term: "Linha", description: "Volume por dia dentro da janela." },
  ],
  fechados: [
    { term: "Fórmula", description: "Vendas confirmadas pelo primeiro recebimento no período." },
    { term: "Não entra", description: "Mensalidades seguintes não contam como novos fechamentos." },
  ],
  conversao: [
    { term: "Fórmula", description: "Negócios fechados ÷ novos leads do período × 100, com os mesmos números dos dois indicadores ao lado." },
    { term: "Leitura", description: "Sem leads, a taxa aparece como —; vendas de cadastros antigos também contam nos negócios fechados." },
    { term: "Comparação", description: "Diferença em pontos percentuais contra o período anterior." },
  ],
} satisfies Record<string, MetricExplanation[]>;

function heatBandLabel(heat: number): string {
  const band = HEAT_BANDS.find((item) => item.id !== "todos" && heat >= item.min && heat <= item.max);
  return band ? band.label : `${heat}`;
}

export function InicioPage() {
  const store = useStore();
  const navigate = useNavigate();
  const identity = useProfileIdentity();
  const [params, setParams] = useSearchParams();
  const now = useMemo(() => new Date().toISOString(), []);

  const periodParam = params.get("periodo");
  const period: PeriodId = isPeriod(periodParam) ? periodParam : DEFAULT_HOME_PERIOD;
  const customFrom = params.get("de");
  const customTo = params.get("ate");
  const range = useMemo(
    () => homeRange(period, now, { from: customFrom, to: customTo }),
    [period, now, customFrom, customTo],
  );
  const [customOpen, setCustomOpen] = useState(false);

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const receipts = useMemo(() => store.receipts ?? [], [store.receipts]);
  const invoices = useMemo(() => store.invoices ?? [], [store.invoices]);
  const revenueGoals = useMemo(() => store.revenueGoals ?? [], [store.revenueGoals]);
  const threads = useMemo(() => store.communicationThreads ?? [], [store.communicationThreads]);
  const activeLeads = useMemo(() => store.leads.filter((lead) => !lead.archived), [store.leads]);
  const kpis = useMemo(() => homeKpis({ leads: activeLeads, receipts: receipts, range }), [activeLeads, receipts, range]);

  const chart = useMemo<ChartData>(() => {
    const firstSales = receipts.filter((receipt) => receipt.firstSale);
    return {
      labels: dayLabels(range),
      rev: kpis.series.revenue.map((cents) => cents / 100),
      leadRevenue: dailySeries(
        range,
        firstSales.map((receipt) => receipt.receivedOn),
        firstSales.map((receipt) => receipt.amountCents),
      ).map((cents) => cents / 100),
      leadCounts: kpis.series.won,
    };
  }, [range, receipts, kpis]);

  const insights = useMemo(
    () =>
      homeInsights({
        leads: activeLeads,
        memberships: store.memberships,
        stages: store.stages,
        invoices: invoices,
        receipts: receipts,
        now,
        range,
        money: moneyCents,
      }),
    [activeLeads, store.memberships, store.stages, invoices, receipts, now, range],
  );

  const funnels = useMemo(() => store.funnels.filter((funnel) => !funnel.archived), [store.funnels]);
  const funnelParam = params.get("funil");
  const funnelId = funnels.some((funnel) => funnel.id === funnelParam) ? funnelParam! : funnels[0]?.id ?? "";
  const flow = useMemo(
    () =>
      funnelId
        ? funnelFlow({ funnelId, stages: store.stages, memberships: store.memberships, leads: activeLeads, events: store.events, range })
        : [],
    [funnelId, store.stages, store.memberships, activeLeads, store.events, range],
  );

  const orbit = useMemo(() => goalOrbit(revenueGoals, receipts, now), [revenueGoals, receipts, now]);
  const tasks = useMemo(() => todayTasks(store.tasks, now), [store.tasks, now]);
  const notices = useMemo(() => store.noticeFeed(), [store]);
  const attention = useMemo(() => attentionItems(notices), [notices]);
  const ranking = useMemo(
    () => topLeads({ leads: store.leads, memberships: store.memberships, stages: store.stages, receipts: receipts, range, now }),
    [store.leads, store.memberships, store.stages, receipts, range, now],
  );

  const leadById = useMemo(() => new Map(store.leads.map((lead) => [lead.id, lead])), [store.leads]);
  const openTasks = tasks.filter((item) => !item.done).length;
  const awaitingReplies = threads.filter(
    (thread) => thread.state !== "resolved" && thread.needsReply && (!thread.leadId || !leadById.get(thread.leadId)?.archived),
  ).length;
  const goalDistance = orbit.goal ? Math.max(0, Math.round((1 - orbit.achievedCents / Math.max(1, orbit.goal.amountCents)) * 100)) : null;

  const name = firstName(identity);
  const hint = periodHint(period, range);

  // O relatório abre a janela do protótipo: modelo, período e a folha do PDF
  // antes de baixar. As contas são as mesmas desta tela (§10).
  const [reportOpen, setReportOpen] = useState(false);

  const revenueValue = useCountUp(Math.round(kpis.revenueCents / 100));
  const leadsValue = useCountUp(kpis.leads);
  const wonValue = useCountUp(kpis.won);

  const sparkColor = (delta: string) => (delta.includes("▼") ? "var(--crit)" : delta.includes("▲") ? "var(--good)" : "var(--muted)");

  const periodChips = PERIOD_OPTIONS.filter((option) => HOME_PERIODS.includes(option.id));

  const openNotice = (notice: Notice) => {
    if (notice.category === "tarefa") navigate(appPath("tarefas"));
    else if (notice.category === "automacao") navigate(appPath("funis"));
    else if (notice.category === "mensagem") navigate(appPath("mensagens"));
    else if (notice.leadId) navigate(appPath(`leads/${notice.leadId}${notice.category === "financeiro" ? "?aba=contratos" : ""}`));
    else navigate(appPath("notificacoes"));
  };

  return (
    <div className="zn-inicio">
      <section className="hero">
        <div>
          <h1>{name ? `Bem-vindo de volta, ${name}.` : "Bem-vindo de volta."}</h1>
          <p>
            Sua constelação está ativa: <b>{openTasks} {openTasks === 1 ? "tarefa" : "tarefas"}</b> para hoje,{" "}
            <b>{awaitingReplies} {awaitingReplies === 1 ? "conversa" : "conversas"}</b> esperando resposta e{" "}
            {goalDistance === null ? (
              <>a meta do trimestre <b>ainda sem valor definido</b>.</>
            ) : (
              <>a meta do trimestre a <b>{goalDistance}%</b> de distância.</>
            )}
          </p>
        </div>
        <div className="hero-actions">
          <div className="chips-wrap">
            <div className="chips" role="group" aria-label="Período" title={rangeLabel(range)}>
              {periodChips.map((option) => {
                const active = option.id === period;
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={active ? "active" : undefined}
                    aria-pressed={active}
                    aria-expanded={option.id === "custom" ? customOpen : undefined}
                    onClick={() => {
                      if (option.id === "custom") {
                        setCustomOpen((value) => !value);
                        return;
                      }
                      setCustomOpen(false);
                      update({ periodo: option.id === DEFAULT_HOME_PERIOD ? null : option.id, de: null, ate: null });
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {customOpen && (
              <CustomPeriod
                from={customFrom}
                to={customTo}
                onApply={(nextFrom, nextTo) => {
                  update({ periodo: "custom", de: nextFrom, ate: nextTo });
                  setCustomOpen(false);
                }}
                onClose={() => setCustomOpen(false)}
              />
            )}
          </div>
          <button type="button" className="btn-primary" onClick={() => setReportOpen(true)}>
            <Plus size={14} aria-hidden="true" /> Novo relatório
          </button>
        </div>
      </section>

      <section className="grid cards-hero" aria-label="Indicadores do período">
        <KpiCard
          label="Receita do período"
          help={KPI_HELP.receita}
          value={money(revenueValue)}
          delta={kpis.revenueDelta}
          tone={deltaTone(kpis.revenueDelta)}
          spark={kpis.series.revenue}
          sparkColor={sparkColor(kpis.revenueDelta)}
          sparkLabel="Receita por dia no período"
          index={0}
          featured
        />
        <KpiCard
          label="Novos leads"
          help={KPI_HELP.leads}
          value={int.format(leadsValue)}
          delta={kpis.leadsDelta}
          tone={deltaTone(kpis.leadsDelta)}
          spark={kpis.series.leads}
          sparkColor={sparkColor(kpis.leadsDelta)}
          sparkLabel="Novos leads por dia no período"
          index={1}
        />
        <KpiCard
          label="Negócios fechados"
          help={KPI_HELP.fechados}
          value={int.format(wonValue)}
          delta={kpis.wonDelta}
          tone={deltaTone(kpis.wonDelta)}
          spark={kpis.series.won}
          sparkColor={sparkColor(kpis.wonDelta)}
          sparkLabel="Negócios fechados por dia no período"
          index={2}
        />
        <KpiCard
          label="Taxa de conversão"
          help={KPI_HELP.conversao}
          value={conversionText(kpis.conversion)}
          delta={kpis.conversionDelta}
          tone={kpis.conversionDelta === "—" ? "flat" : kpis.conversionUp ? "up" : "down"}
          spark={kpis.series.conversion}
          sparkColor={kpis.conversionUp ? "var(--good)" : kpis.conversionDelta === "—" ? "var(--muted)" : "var(--crit)"}
          sparkLabel="Conversão acumulada no período"
          index={3}
        />
      </section>

      <section className="grid row-main">
        <div className="card chart-wrap">
          <div className="card-title">
            <div>
              <div className="card-heading-row">
                <h2>Visão da receita</h2>
                <div className="chart-legend">
                  <span>
                    <i className="sw-rev" />
                    Receita
                  </span>
                  <span>
                    <i className="sw-leads" />
                    Leads
                  </span>
                </div>
              </div>
              <div className="chart-sub">
                total recebido e primeiras mensalidades · <span>{hint} · pagamentos recebidos</span>
              </div>
            </div>
            <HelpPopover
              about="a visão da receita"
              label="O que este bloco mede: Visão da receita"
              align="end"
              items={[
                { term: "Receita", description: "Total recebido por dia, na área azul." },
                { term: "Leads", description: "Recebimentos da primeira mensalidade dos novos clientes, na mesma escala em reais; o marcador informa quantos cadastros contribuíram." },
                { term: "Não entra", description: "Extras e avulsos entram só na Receita." },
              ]}
            />
          </div>
          <RevenueChart data={chart} />
        </div>

        <div className="card ai-panel" aria-label="Insights da constelação">
          <div className="card-title">
            <div className="ai-head">
              <div className="ai-spark" aria-hidden="true">
                <CosmosAiIcon size={15} />
              </div>
              <h2>Insights da constelação</h2>
            </div>
            <HelpPopover
              about="os insights"
              label="O que este bloco faz: Insights da constelação"
              align="end"
              items={[
                { term: "Previsão", description: "Soma das faturas em aberto com vencimento nos próximos 30 dias, pelos contratos vigentes." },
                { term: "Risco", description: "Propostas ativas há mais de 5 dias sem interação real, com o valor potencial somado." },
                { term: "Oportunidade", description: "Origem com maior conversão no período entre as que trouxeram ao menos 5 leads." },
                { term: "Método", description: "Regras publicadas sobre seus dados; nenhum modelo de IA participa do cálculo." },
              ]}
            />
            <span className="live-badge">ao vivo</span>
          </div>
          <div className="ins-list">
            {insights.map((insight) => (
              <div className="ins" key={insight.key}>
                <p>
                  {insight.parts.map((part, index) =>
                    part.strong ? (
                      <b key={index} className={part.numeric ? "num" : undefined}>
                        {part.text}
                      </b>
                    ) : (
                      <span key={index}>{part.text}</span>
                    ),
                  )}
                </p>
                <span className={`ins-chip ${insight.tone}`}>{insight.chip}</span>
              </div>
            ))}
          </div>
          <div className="ai-ask" title="Perguntas à IA ficam indisponíveis até a integração ser ativada; as sugestões da IA existem só na Inbox, no ambiente local.">
            <input
              type="text"
              placeholder="Pergunte à IA qualquer coisa…"
              aria-label="Pergunta para a IA (indisponível)"
              disabled
            />
            <button type="button" className="ask-send" aria-label="Enviar pergunta (indisponível)" disabled>
              <Send size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      <section className="grid row-mid">
        <div className="card funnel-card" aria-label="Conversão do funil">
          <div className="card-title">
            <h2>Conversão do funil</h2>
            <HelpPopover
              about="a conversão do funil"
              label="O que este bloco mede: Conversão do funil"
              items={[
                { term: "Fluxo", description: "Participações criadas no período que chegaram a cada etapa; a espessura mostra quanto do topo continua vivo." },
                { term: "Tempo", description: "Abaixo, a média de dias nas visitas já encerradas de cada etapa." },
                { term: "Escopo", description: "Somente o funil escolhido no seletor." },
              ]}
            />
            {funnels.length > 0 && (
              <CompactSelect
                className="zn-home-funnel"
                prefix="Funil"
                options={funnels.map((funnel) => ({ value: funnel.id, label: funnel.name }))}
                value={funnelId}
                align="end"
                onChange={(value) => update({ funil: value === funnels[0]?.id ? null : value })}
              />
            )}
          </div>
          {flow.length ? (
            <>
              <div className="fn-cols" style={{ gridTemplateColumns: `repeat(${flow.length}, 1fr)` }}>
                {flow.map((item, index) => (
                  <div className="fn-col" key={item.stage.id}>
                    <div className="l">{item.stage.name}</div>
                    <div className="v num" style={{ "--fn-delay": `${stageDelay(index / flow.length)}ms` } as CSSProperties}>
                      {Math.round(item.ratio * 100)}%<span className="p">{int.format(item.reached)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <FunnelChart ratios={flow.map((item) => item.ratio)} />
              <div className="fn-cols fn-durs" style={{ gridTemplateColumns: `repeat(${flow.length}, 1fr)` }}>
                {flow.map((item) => (
                  <div className="fn-col" key={item.stage.id}>
                    {item.averageDays === null ? "—" : `média ${item.averageDays} d`}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="zn-home-empty">Nenhum funil ativo com etapas para medir.</p>
          )}
        </div>

        <div className="card orbit-card">
          <div className="card-title">
            <h2>Meta do trimestre</h2>
            <HelpPopover
              about="a meta do trimestre"
              label="O que este bloco mede: Meta do trimestre"
              items={[
                { term: "Esquerda", description: "Quanto da meta já foi recebido no trimestre." },
                { term: "Direita", description: "Quanto do trimestre já passou; se os dois anéis andam juntos, você está no ritmo esperado." },
              ]}
            />
            <span className="hint">{orbit.label}</span>
          </div>
          <div className="orbit-duo">
            <div className="orbit-half">
              <OrbitGauge ratio={orbit.achievedRatio} colors={["var(--grad-a)", "var(--grad-b)"]} sub="da meta" />
              <OrbitFootGoal orbit={orbit} />
            </div>
            <div className="orbit-half">
              <OrbitGauge ratio={orbit.elapsedRatio} colors={["var(--warn)", "var(--warn)"]} sub="do trimestre" />
              <OrbitFootPace orbit={orbit} />
            </div>
          </div>
        </div>
      </section>

      <section className="grid row-bottom">
        <div className="card card-tasks">
          <div className="card-title">
            <h2>Hoje</h2>
            <button type="button" className="mini-btn" aria-label="Abrir guia de tarefas" onClick={() => navigate(appPath("tarefas"))}>
              <ArrowUpRight size={12} aria-hidden="true" />
            </button>
            <HelpPopover
              about="as tarefas de hoje"
              label="O que este bloco mede: Hoje"
              align="end"
              items={[
                { term: "Escopo", description: "Suas tarefas com vencimento hoje, no fuso de São Paulo." },
                { term: "Concluir", description: "Marcar a caixa conclui a tarefa de verdade; ela desce para o fim da lista." },
              ]}
            />
            <span className="hint">{todayHint(now)}</span>
          </div>
          <div className="zn-tasks">
            {tasks.length === 0 && <p className="zn-home-empty">Nenhuma tarefa vence hoje.</p>}
            {tasks.map((item, index) => {
              const lead = item.task.leadId ? leadById.get(item.task.leadId) : undefined;
              return (
                <div
                  key={item.task.id}
                  className={item.done ? "zn-task done" : "zn-task"}
                  style={{ animation: `rise .5s cubic-bezier(.2,.7,.3,1) ${index * 50}ms both` }}
                >
                  <input
                    className="chk"
                    type="checkbox"
                    checked={item.done}
                    aria-label={`${item.done ? "Reabrir" : "Concluir"}: ${item.task.description}`}
                    onChange={() => void store.toggleTask(item.task.id)}
                  />
                  <span className={item.late ? "task-hour num late" : "task-hour num"}>{item.hour}</span>
                  <div className="task-body">
                    <div className="t">{item.task.description}</div>
                    <div className="d">
                      {lead ? `${lead.name} · ${lead.company}` : "interno"}
                      {item.late && <span className="late-tag"> · vencida</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {tasks.length > 0 && (
            <div className="tasks-foot">
              <b>{tasks.length - openTasks}</b> de {tasks.length} concluídas
              {openTasks === 0 ? " — tudo em dia por hoje." : " — o dia ainda tem espaço."}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title">
            <h2>Requer atenção</h2>
            <button type="button" className="mini-btn" aria-label="Abrir central de avisos" onClick={() => navigate(appPath("notificacoes"))}>
              <ArrowUpRight size={12} aria-hidden="true" />
            </button>
            <HelpPopover
              about="o que requer atenção"
              label="O que este bloco mede: Requer atenção"
              align="end"
              items={[
                { term: "Fonte", description: "Os avisos acionáveis e não resolvidos da central: tarefas vencidas, faturas atrasadas, leads esfriando, conversas sem resposta e incidentes." },
                { term: "Ordem", description: "Críticos primeiro, depois atenção, depois informativos; os quatro mais recentes." },
              ]}
            />
            <span className="hint">{attention.total} {attention.total === 1 ? "item" : "itens"}</span>
          </div>
          <div className="att">
            {attention.items.length === 0 && <p className="zn-home-empty">Nada requer atenção agora.</p>}
            {attention.items.map((item) => (
              <div className="att-item" key={item.notice.key}>
                <div className={`att-ico ${item.tone}`} aria-hidden="true">
                  <NoticeIcon category={item.notice.category} />
                </div>
                <div className="att-body">
                  <div className="t">{item.notice.title}</div>
                  <div className="d">{item.notice.detail}</div>
                </div>
                <button type="button" className="btn-ghost" onClick={() => openNotice(item.notice)}>
                  {item.action}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-title">
            <h2>Top leads</h2>
            <button type="button" className="mini-btn" aria-label="Abrir leads" onClick={() => navigate(appPath("clientes"))}>
              <ArrowUpRight size={12} aria-hidden="true" />
            </button>
            <HelpPopover
              about="os top leads"
              label="O que este bloco mede: Top leads"
              align="end"
              items={[
                { term: "Ordem", description: "Os clientes que mais renderam no período selecionado." },
                { term: "Valor", description: "LTV: tudo que o cliente já rendeu até hoje, não só no período." },
                { term: "Saúde", description: "Faixa do AI Score do lead: 80 ou mais é saudável, 60 a 79 pede atenção, abaixo está em risco." },
              ]}
            />
          </div>
          <div className="tl-list">
            {ranking.length === 0 && <p className="zn-home-empty">Nenhum recebimento no período.</p>}
            {ranking.map((item) => {
              const health = healthOf(item.heat);
              return (
                <div
                  className="tl-lead"
                  key={item.lead.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Abrir perfil de ${item.lead.name}`}
                  onClick={() => navigate(appPath(`leads/${item.lead.id}`))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigate(appPath(`leads/${item.lead.id}`));
                    }
                  }}
                >
                  <span className="lead-ava" style={{ background: avatarGradient(item.lead.name) }} aria-hidden="true">
                    {avatarInitials(item.lead.name)}
                  </span>
                  <div className="tl-info">
                    <div className="nm">{item.lead.name}</div>
                    <div className="co">{item.lead.company}</div>
                  </div>
                  <span className="zn-stage-pill" style={{ "--cx-dot": paletteColor(item.stage?.color) } as CSSProperties}>
                    <i aria-hidden="true" />
                    {item.stage?.name ?? "Sem etapa"}
                  </span>
                  <span className="tl-val num">{moneyCents(item.ltvCents)}</span>
                  <span className={`health ${health.tone}`} title={`AI Score ${item.heat} · ${heatBandLabel(item.heat)}`}>
                    {health.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {reportOpen && <ReportDialog onClose={() => setReportOpen(false)} period={period} funnelId={funnelId} />}
    </div>
  );
}

